import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import DataLoader from "../../widgets/DataLoader";
import { useBusinessSettings } from "../../contexts/BusinessSettingsContext";
import { useUser } from "../../contexts/UserInfoContext";
import OneSignal from "react-onesignal";
import { jwtDecode } from "jwt-decode";
import { useOneSignal } from "../../App";

interface DecodedToken {
  sub: string;
  name?: string;
  mobile_number?: string;
  email?: string;
  [key: string]: any;
}

export default function TokenHandlerComponent() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const tokenFromUrl = searchParams.get("access_token");
  const { fetchSettings } = useBusinessSettings();
  const { fetchUserInfo } = useUser();
  const hasInitialized = useRef(false);
  const { isInitialized: isOneSignalInitialized } = useOneSignal();

  useEffect(() => {
    async function fetchToken() {
      try {
        if (!tokenFromUrl) {
          console.error("No access_token in URL");
          navigate("/login");
          return;
        }

        console.log("✅ Token found in URL, storing in localStorage");

        // Step 1: Store token in localStorage as auth_token
        localStorage.setItem("auth_token", tokenFromUrl);

        // Dispatch custom event to notify contexts (storage event doesn't fire in same window)
        window.dispatchEvent(
          new CustomEvent("auth_token_changed", {
            detail: { token: tokenFromUrl },
          })
        );

        console.log(
          "✅ Token stored, fetching business settings and user info..."
        );

        // Step 2: Fetch both business settings and user info in parallel
        await Promise.all([fetchSettings(), fetchUserInfo()]);

        console.log("✅ All data loaded successfully, navigating to dashboard");

        // Step 3: Only navigate after all data is loaded
        navigate("/dashboard");
      } catch (error) {
        console.error("❌ Error during token handling:", error);
        // Clear potentially invalid token
        localStorage.removeItem("auth_token");
        navigate("/login");
      } finally {
        setLoading(false);
      }
    }
    fetchToken();
  }, [tokenFromUrl, navigate, fetchSettings, fetchUserInfo]);

  // Initialize OneSignal user after token is stored and OneSignal is ready
  useEffect(() => {
    // Prevent multiple initializations
    if (hasInitialized.current) {
      console.log("OneSignal user setup already completed");
      return;
    }

    const initializeOneSignalUser = async () => {
      // Wait for OneSignal initialization
      if (!isOneSignalInitialized) {
        console.log("OneSignal not ready yet");
        return;
      }

      // Additional check to ensure OneSignal is fully loaded
      if (
        typeof OneSignal === "undefined" ||
        !OneSignal.User ||
        !OneSignal.Notifications
      ) {
        console.log("OneSignal SDK not fully loaded yet");
        return;
      }

      try {
        // Get access token from localStorage
        const authToken = localStorage.getItem("auth_token");

        if (!authToken) {
          console.log("No auth token found - user not logged in");
          return;
        }

        // Decode the token
        const decoded: DecodedToken = jwtDecode(authToken);

        if (!decoded.sub) {
          console.error("No sub found in decoded token");
          return;
        }

        console.log("Setting up OneSignal for user:", decoded.sub);

        // Check current external ID - using optional chaining for safety
        const currentExternalId = OneSignal?.User?.externalId;

        if (currentExternalId && currentExternalId === decoded.sub) {
          console.log("User already logged in to OneSignal");

          // Check permission status using permissionNative (synchronous)
          const permission = OneSignal.Notifications.permissionNative;

          if (permission === "default") {
            // Permission not yet requested
            try {
              console.log("Showing permission prompt...");
              await OneSignal.Slidedown.promptPush();
            } catch (promptError: any) {
              // Handle permission blocked or slidedown errors gracefully
              if (promptError?.message?.includes("blocked")) {
                console.log("Notification permission is blocked by user");
              } else {
                console.error("Error showing permission prompt:", promptError);
              }
            }
          } else if (permission === "granted") {
            console.log("Notifications already enabled");
          } else {
            console.log("Notifications denied by user");
          }

          hasInitialized.current = true;
          return;
        }

        // Wait additional time to ensure SDK is fully ready before login
        await new Promise((resolve) => setTimeout(resolve, 1500));

        // Double-check OneSignal context is ready
        if (!OneSignal.User || !OneSignal.login) {
          console.error(
            "OneSignal SDK lost readiness, skipping initialization"
          );
          hasInitialized.current = true;
          return;
        }

        // Login to OneSignal
        console.log("Logging in to OneSignal...");
        await OneSignal.login(decoded.sub);
        console.log("OneSignal login successful");

        // Set user tags/properties
        await OneSignal.User.addTags({
          name: decoded.name || "",
          mobile_number: decoded.mobile_number || "",
          email: decoded.email || "",
          login_date: new Date().toISOString(),
        });

        console.log("User tags set successfully");

        // Check permission and show prompt if needed (using permissionNative)
        const permission = OneSignal.Notifications.permissionNative;

        if (permission === "default") {
          // Small delay for better UX
          setTimeout(async () => {
            try {
              console.log("Showing permission prompt...");
              await OneSignal.Slidedown.promptPush();
            } catch (promptError: any) {
              // Handle permission blocked or slidedown errors gracefully
              if (promptError?.message?.includes("blocked")) {
                console.log("Notification permission is blocked by user");
              } else {
                console.error("Error showing permission prompt:", promptError);
              }
            }
          }, 2000);
        } else if (permission === "granted") {
          console.log("Notifications already enabled");
        } else {
          console.log("Notifications denied by user");
        }

        hasInitialized.current = true;
      } catch (error) {
        console.error("Error initializing OneSignal user:", error);
        if (error instanceof Error) {
          console.error("Error details:", error.message);
        }
        hasInitialized.current = true; // Prevent retry loops
      }
    };

    initializeOneSignalUser();
  }, [isOneSignalInitialized]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <DataLoader />
      </div>
    );
  }

  return null;
}
